import {
  AddRestrictedWordsResponse,
  FetchRestrictedGroupsResponse,
  FetchRestrictedWordResponse,
  FetchRestrictedWordsResponse,
  ResetGroupsResponse,
  RestrictedWord,
  AddRestrictionGroupsPayload,
  UpdateRestrictedWordPayload,
  RestrictedWordBase,
  UpdateWordPolicyPayload,
  FetchGroupArticlesResponse,
  DeleteGroupPayload,
  AssignGroupToArticlePayload
} from './restrictedWordsTypes';
import { DEFAULT_WORD_POLICY } from './restrictedWordsConstants';
import request from '@host-panel/request';
import { isGlobalGroup } from './restrictedWordsUtils';

const rootPath = '/host-panel/v2/restricted';

export async function fetchGlobalRestrictedWordsRequest(): Promise<FetchRestrictedWordsResponse> {
  return await request(`${rootPath}/global/words`, {});
}

export async function fetchRestrictedWordsByGroupRequest(
  groupId: number
): Promise<FetchRestrictedWordsResponse> {
  return await request(`${rootPath}/groups/${groupId}/words`, {});
}

export async function fetchSearchRestrictedWordsRequest(): Promise<FetchRestrictedWordsResponse> {
  return await request(`${rootPath}/words`, {});
}

export async function updateWordPolicyRequest(payload: UpdateWordPolicyPayload): Promise<void> {
  const { word, groupId } = payload;
  const { value, policy, exceptions } = word;

  const isGlobal = isGlobalGroup(groupId);
  const url = isGlobal
    ? `${rootPath}/words/${word.id}`
    : `${rootPath}/groups/${groupId}/words/${word.id}/policy`;

  return await request(url, {
    method: isGlobal ? 'PUT' : 'POST',
    data: { text: value, policy, exceptions }
  });
}

export async function fetchRestrictedGroupsRequest(): Promise<FetchRestrictedGroupsResponse> {
  return await request(`${rootPath}/groups`, {});
}

export async function addGlobalRestrictedWordsRequest(
  words: Array<string>
): Promise<AddRestrictedWordsResponse> {
  const newWords = getNewWords(words);

  return await request(`${rootPath}/words`, { method: 'POST', data: newWords });
}

export async function deleteGlobalRestrictedWordsRequest(
  words: Array<RestrictedWord>
): Promise<void> {
  const ids = getIds(words);

  return await request(`${rootPath}/delete-words`, { method: 'POST', data: { ids } });
}

export async function makesRestrictedWordsGlobalRequest(
  words: Array<RestrictedWord>
): Promise<ResetGroupsResponse> {
  const ids = getIds(words);

  return await request(`${rootPath}/reset-groups`, { method: 'POST', data: { words_ids: ids } });
}

export async function addRestrictedWordsToGroupRequest(
  groupId: number,
  existingWords: Array<RestrictedWordBase>,
  newWords: Array<string> = []
): Promise<AddRestrictedWordsResponse> {
  const newWordsPayload = getNewWords(newWords);
  const existingIds = existingWords.map(({ id, policy }) => ({ id, policy }));

  const payload = {};
  if (newWordsPayload.length) {
    payload['to_create'] = newWordsPayload;
  }
  if (existingIds.length) {
    payload['to_add'] = existingIds;
  }

  return await request(`${rootPath}/groups/${groupId}/words`, {
    method: 'POST',
    data: payload
  });
}

export async function deleteRestrictedWordsFromGroupRequest(
  words: Array<RestrictedWord>,
  groupId: number
) {
  const ids = getIds(words);

  return await request(`${rootPath}/groups/${groupId}/words`, {
    method: 'POST',
    data: { to_drop: ids }
  });
}

export async function fetchRestrictedWordRequest(
  wordId: number
): Promise<FetchRestrictedWordResponse> {
  return await request(`${rootPath}/words/${wordId}`, {});
}

export async function fetchRestrictionGroupsRequest(
  wordId: number
): Promise<FetchRestrictedGroupsResponse> {
  return await request(`${rootPath}/groups?word_id=${wordId}`, {});
}

export async function updateRestrictedWordRequest(
  word: UpdateRestrictedWordPayload,
  id: number
): Promise<void> {
  return await request(`${rootPath}/words/${id}`, {
    method: 'PUT',
    data: word
  });
}

export async function deleteRestrictedWordsRequest(words: Array<RestrictedWord>) {
  const ids = getIds(words);

  return await request(`${rootPath}/delete-words`, {
    method: 'POST',
    data: { ids }
  });
}

export async function addRestrictionGroupsRequest(
  id: number,
  restrictionGroups: AddRestrictionGroupsPayload
): Promise<void> {
  const payload = {
    to_create: restrictionGroups.toCreate?.length
      ? restrictionGroups.toCreate.map(({ value, policy }) => ({
          name: value,
          policy
        }))
      : [],
    to_add: restrictionGroups.toAdd?.length
      ? restrictionGroups.toAdd.map(({ id, policy }) => ({ id, policy }))
      : [],
    to_drop: restrictionGroups.toDelete?.length
      ? restrictionGroups.toDelete.map(({ id }) => id)
      : []
  };

  return await request(`${rootPath}/words/${id}/groups`, {
    method: 'POST',
    data: payload
  });
}

export async function fetchGroupArticlesRequest(
  groupId: number
): Promise<FetchGroupArticlesResponse> {
  return await request(`${rootPath}/groups/${groupId}/articles`, {});
}

export async function deleteGroupRequest(payload: DeleteGroupPayload): Promise<void> {
  const group = {
    group_id: payload.groupId,
    delete_words_flag: payload.deleteWordsFlag
  };

  return await request(`${rootPath}/delete-group`, {
    method: 'POST',
    data: group
  });
}

export async function updateRestrictedGroupRequest(groupId: number, name: string): Promise<void> {
  return await request(`${rootPath}/groups/${groupId}`, {
    method: 'PUT',
    data: { name }
  });
}

export async function assignGroupToArticleRequest(
  payload: AssignGroupToArticlePayload
): Promise<void> {
  const assignedGroups = {
    post_id: payload.postId,
    article_name: payload.articleName,
    groups_ids: payload.restrictedGroups?.map(group => group.id)
  };

  return await request(`${rootPath}/assignments/set`, {
    method: 'POST',
    data: assignedGroups
  });
}

function getNewWords(words: Array<string>) {
  return words.map(word => ({
    text: word,
    policy: DEFAULT_WORD_POLICY
  }));
}

function getIds(words: Array<RestrictedWord>) {
  return words.map(word => word.id);
}
